import styled from 'styled-components'
import { StatusKanbanSearchCondition } from '../../hooks/useSearchCondition'
import { intl } from '../../../../../i18n'
import { useCallback } from 'react'
import { Button } from '../../../../components/buttons'
import { useDialogState } from '../../../../components/dialogs/useDialogState'
import { colorPalette } from '../../../../style/colorPalette'
import ReloadIcon from '../../../../components/icons/ReloadIcon'
import { Switch, Typography } from '@mui/material'
import { FontSize } from '../../../../../styles/commonStyles'
import CancelConfirmDialog from '../../../../components/dialogs/CancelConfirmDialog'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../store'
import Filters from '../Filter'
import { FilterModel } from '../Filter/filterModel'
import { SortConfig } from '../../hooks/useSort'
import { SortButton } from '../Sort'
import { ProjectPlanBreadcrumbs } from '../../../../components/navigation/project-plan-breadcrumbs'
import { getWbsItemUuidByProjectPlanUuid } from '../../../../../lib/functions/projectPlan'
import { StoredWbsItemSearchCondition } from '../../../../../services/model/storedWbsItemSearchCondition'
import { useWbsItemSearchConditionSerializeService } from '../../../../../services/transform-service/wbsItemSearchConditionSerializeService'
import { toUrlQuery } from '../../../../../lib/commons/api'
import SearchConditionPopper from './SearchConditionPopper'
import SearchConditionLabels from './SearchConditionLabels'

type Props = {
  searchCondition: StatusKanbanSearchCondition
  onRootChange: (rootUuid: string | undefined) => void
  onCancel: () => void
  onSearch: (condition?: StatusKanbanSearchCondition) => Promise<boolean>
  filterModels: FilterModel[]
  updateFilterModel: (filterModel: FilterModel) => void
  applySort: (sortConfig: SortConfig | null) => void
  currentSortConfig: SortConfig | null
  rootProjectPlanUuid?: string
  isSwimlaneEnabled: boolean
  onToggleSwimlaneMode: () => void
  defaultOpenSearchCondition?: boolean
}

const HeaderComponent = ({
  searchCondition,
  onRootChange,
  onCancel,
  onSearch,
  filterModels,
  updateFilterModel,
  applySort,
  currentSortConfig,
  rootProjectPlanUuid,
  isSwimlaneEnabled,
  onToggleSwimlaneMode,
  defaultOpenSearchCondition,
}: Props) => {
  const [showCancelDialog, openCancelDialog, closeCancelDialog] =
    useDialogState()
  const hasRequiredSaveData = useSelector<AllState, boolean>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const onReload = useCallback(() => {
    if (hasRequiredSaveData) {
      openCancelDialog()
    } else {
      onCancel()
    }
  }, [hasRequiredSaveData, onCancel])

  const transformer = useWbsItemSearchConditionSerializeService()
  const switchRootWbsItem = useCallback(
    async (newProjectPlanRootUuid: string | undefined) => {
      const newRootUuid = newProjectPlanRootUuid
        ? await getWbsItemUuidByProjectPlanUuid(newProjectPlanRootUuid)
        : undefined
      const plainObject: StoredWbsItemSearchCondition & { rootUuid?: string } =
        transformer.fromVoToStoredObject(searchCondition)
      plainObject.rootUuid = newRootUuid
      const query = toUrlQuery(plainObject)

      onRootChange(newRootUuid)
      history.replaceState(undefined, '', `?${query}`)
    },
    [searchCondition]
  )

  return (
    <HeaderRoot>
      <ProjectPlanBreadcrumbs
        rootProjectPlanUuid={rootProjectPlanUuid}
        onSelectBreadcrumb={switchRootWbsItem}
      />
      <SearchConditionLabels searchCondition={searchCondition} />
      <OtherHeaderArea>
        <ReloadButton onClick={onReload}>
          <ReloadIcon />
        </ReloadButton>
        <SearchConditionPopper
          searchCondition={searchCondition}
          onSearch={onSearch}
          defaultOpen={defaultOpenSearchCondition}
        />
        <SwimlaneToggle>
          <SwimlaneToggleLabel>
            {intl.formatMessage({ id: 'kanban.groupByResponsible' })}
          </SwimlaneToggleLabel>
          <Switch
            checked={isSwimlaneEnabled}
            onChange={onToggleSwimlaneMode}
            size="small"
          />
        </SwimlaneToggle>
        <Filters
          filterModels={filterModels}
          updateFilterModel={updateFilterModel}
        />
        <SortButton
          applySort={applySort}
          currentSortConfig={currentSortConfig}
        />
      </OtherHeaderArea>
      <CancelConfirmDialog
        open={showCancelDialog}
        onConfirm={() => {
          closeCancelDialog()
          onCancel()
        }}
        onClose={closeCancelDialog}
      />
    </HeaderRoot>
  )
}

export default HeaderComponent

const HeaderRoot = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
  padding: '8px 8px 0',
  gap: '8px',
  overflowX: 'auto',
  minHeight: '96px',
  position: 'relative',
  zIndex: 2,
})

const OtherHeaderArea = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '24px',
  minWidth: 'fit-content',
})

const ReloadButton = styled(Button)({
  background: colorPalette.monotone[1],
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  padding: '5px 8px',
  cursor: 'pointer',
  '&:hover': { background: colorPalette.monotone[1] },
})

const SwimlaneToggle = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  minWidth: 'fit-content',
})

const SwimlaneToggleLabel = styled(Typography)({
  color: colorPalette.monotone[5],
  fontSize: FontSize.MEDIUM,
  minWidth: 'fit-content',
})
